import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Truck, MapPin, Search, Filter, Send, X, Calendar, Package, ChevronDown, Facebook, Share2 } from 'lucide-react';
import { loadGoogleMaps } from '../utils/googleMapsLoader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRAILER_LABELS = {
  flat_top: 'Flat Top', drop_deck: 'Drop Deck', low_loader: 'Low Loader',
  tautliner: 'Tautliner', refrigerated: 'Refrigerated', tanker: 'Tanker',
  side_loader: 'Side Loader', skel_container: 'Skel/Container', float: 'Float',
  extendable: 'Extendable', b_double: 'B-Double', road_train: 'Road Train',
  tilt_tray: 'Tilt Tray', car_carrier: 'Car Carrier', livestock: 'Livestock',
  bulk_tipper: 'Bulk Tipper', walking_floor: 'Walking Floor', other: 'Other'
};

const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

const Transporters = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTown, setSearchTown] = useState('');
  const [searchRadius, setSearchRadius] = useState(200);
  const [searchTrailer, setSearchTrailer] = useState('');
  const [searchCoords, setSearchCoords] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [availableOnly, setAvailableOnly] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [requestForm, setRequestForm] = useState({
    shipper_email: '', shipper_name: '', shipper_phone: '', shipper_company: '',
    pickup_location: '', dropoff_location: '', cargo_type: '', cargo_description: '',
    weight: '', dimensions: '', pickup_date: '', message: ''
  });

  useEffect(() => { fetchListings(); }, []);

  const fetchListings = async (coords, trailer, radius) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const c = coords || searchCoords;
      const r = radius || searchRadius;
      const t = trailer !== undefined ? trailer : searchTrailer;
      if (c) {
        params.append('lat', c.lat);
        params.append('lng', c.lng);
        params.append('radius_km', r);
      }
      if (t) params.append('trailer_type', t);
      const res = await axios.get(`${API}/transporter-listings?${params.toString()}`);
      setListings(res.data);
    } catch {
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchTown.trim()) {
      setGeocoding(true);
      try {
        const res = await axios.get(`${API}/geocode?town=${encodeURIComponent(searchTown.trim())}`);
        const coords = { lat: res.data.lat, lng: res.data.lng };
        setSearchCoords(coords);
        await fetchListings(coords, searchTrailer, searchRadius);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Could not find that location. Try adding a state, e.g. "Dublin, SA"');
        // Fall back to listing all
        setSearchCoords(null);
        await fetchListings(null, searchTrailer, searchRadius);
      } finally {
        setGeocoding(false);
      }
    } else {
      setSearchCoords(null);
      await fetchListings(null, searchTrailer, searchRadius);
    }
  };

  const handleClearSearch = () => {
    setSearchTown('');
    setSearchCoords(null);
    setSearchTrailer('');
    setSearchRadius(200);
    fetchListings(null, '', 200);
  };

  const openRequestModal = (listing) => {
    setSelectedListing(listing);
    const email = localStorage.getItem('user_email') || '';
    setRequestForm(prev => ({ ...prev, shipper_email: email }));
    setShowRequestModal(true);
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!requestForm.shipper_email || !requestForm.shipper_name || !requestForm.pickup_location || !requestForm.dropoff_location || !requestForm.cargo_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/transporter-requests`, {
        listing_id: selectedListing.id,
        ...requestForm
      });
      toast.success('Request submitted! The carrier will be notified and can respond with a price.');
      setShowRequestModal(false);
      setRequestForm({ shipper_email: '', shipper_name: '', shipper_phone: '', shipper_company: '', pickup_location: '', dropoff_location: '', cargo_type: '', cargo_description: '', weight: '', dimensions: '', pickup_date: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d) => { if (!d) return ''; try { return new Date(d).toLocaleDateString('en-AU'); } catch { return d; } };

  // Client-side filter by "available to load"
  const filteredListings = availableOnly ? listings.filter(l => l.is_available_to_load !== false) : listings;

  // Load Google Maps
  useEffect(() => {
    loadGoogleMaps().then(() => setMapLoaded(true)).catch(() => {});
  }, []);

  // Render markers
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    if (!mapInstance.current) {
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: -25.2744, lng: 133.7751 },
        zoom: 4,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
      });
    }
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    filteredListings.forEach(l => {
      if (l.location_lat && l.location_lng) {
        const busy = l.is_available_to_load === false;
        const marker = new window.google.maps.Marker({
          position: { lat: l.location_lat, lng: l.location_lng },
          map: mapInstance.current,
          title: `${l.company_name}${l.fleet_number ? ` [#${l.fleet_number}]` : ''} — ${l.location_suburb}, ${l.location_state}${busy ? ' (Not available)' : ''}`,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: busy ? '#9ca3af' : '#dc2626',
            fillOpacity: 0.9,
            strokeColor: '#fff',
            strokeWeight: 2,
          },
        });
        marker.addListener('click', () => {
          setSelectedListing(l);
          setShowRequestModal(true);
        });
        markersRef.current.push(marker);
      }
    });
  }, [mapLoaded, filteredListings]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <div className="bg-[#1e3a5f] text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <Truck className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Transporters Ready to Load</h1>
          <p className="text-lg text-blue-200 max-w-2xl mx-auto mb-5">
            Browse available carriers ready to transport your freight. Search by location and trailer type, then request a quote directly.
          </p>

          {/* For Carriers / For Shippers */}
          <div className="inline-flex gap-2 rounded-lg p-1 bg-white/10 backdrop-blur">
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/20 hover:text-white cursor-default"
              disabled
              data-testid="audience-shipper"
            >
              I&apos;m a Shipper — browsing (current view)
            </Button>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => {
                const email = localStorage.getItem('user_email');
                const roles = (() => { try { return JSON.parse(localStorage.getItem('user_roles') || '[]'); } catch { return []; } })();
                if (!email) {
                  navigate('/login?next=/carrier-dashboard%3Ftab%3Davailability');
                } else if (!roles.includes('carrier')) {
                  navigate('/signup?role=carrier');
                } else {
                  navigate('/carrier-dashboard?tab=availability');
                }
              }}
              data-testid="audience-carrier"
            >
              I&apos;m a Carrier — post my availability →
            </Button>
          </div>
        </div>
      </div>

      {/* Carrier Pricing Callout — visible on public page to convert ad-hoc drivers before the paywall */}
      <div className="container mx-auto px-4 mt-8">
        <div
          className="relative rounded-2xl shadow-lg border border-blue-200 bg-gradient-to-r from-blue-50 via-white to-emerald-50"
          data-testid="carrier-pricing-callout"
        >
          <div className="absolute -top-3 left-6 bg-red-600 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow z-10">
            Are you a carrier?
          </div>
          <div className="grid md:grid-cols-[1fr_auto] items-center gap-4 p-5 sm:p-6">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-[#1e3a5f] mb-1">
                Get your trucks on this map from just <span className="text-blue-700">$1.99/week</span>
              </h3>
              <p className="text-sm text-gray-700 mb-3">
                Ad-hoc drivers pay <strong>$4.99 per truck</strong> for a 7-day pass, or
                <strong> $1.99/wk</strong> covers up to <strong>10 trucks</strong> — just
                <strong> +$0.50/wk</strong> each additional. <span className="text-emerald-700 font-semibold">Free</span> for active platform subscribers.
              </p>
              <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Instant map visibility
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Direct shipper enquiries
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Cancel weekly plan anytime
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-2 items-stretch md:min-w-[180px]">
              <Button
                onClick={() => {
                  const email = localStorage.getItem('user_email');
                  const roles = (() => { try { return JSON.parse(localStorage.getItem('user_roles') || '[]'); } catch { return []; } })();
                  if (!email) {
                    navigate('/login?next=/carrier-dashboard%3Ftab%3Davailability');
                  } else if (!roles.includes('carrier')) {
                    navigate('/signup?role=carrier');
                  } else {
                    navigate('/carrier-dashboard?tab=availability');
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white shadow"
                data-testid="pricing-cta-list-truck"
              >
                List my truck
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/signup?role=carrier')}
                className="border-blue-500 text-blue-700 hover:bg-blue-50"
                data-testid="pricing-cta-signup"
              >
                Sign up as carrier
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="container mx-auto px-4 mt-6 relative z-10">
        <Card className="p-4 sm:p-6 shadow-lg">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-[2]">
                <Label className="text-xs text-gray-500 mb-1">Town / Suburb</Label>
                <Input
                  value={searchTown}
                  onChange={(e) => setSearchTown(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="e.g. Dublin, SA or Melbourne"
                  data-testid="search-town"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-gray-500 mb-1">Radius: {searchRadius} km</Label>
                <input
                  type="range"
                  min="50" max="500" step="50"
                  value={searchRadius}
                  onChange={(e) => setSearchRadius(parseInt(e.target.value))}
                  className="w-full h-10 accent-red-600"
                  data-testid="search-radius"
                />
                <div className="flex justify-between text-[10px] text-gray-400 -mt-1">
                  <span>50km</span><span>250km</span><span>500km</span>
                </div>
              </div>
              <div className="flex-1">
                <Label className="text-xs text-gray-500 mb-1">Trailer Type</Label>
                <select value={searchTrailer} onChange={(e) => setSearchTrailer(e.target.value)} className="w-full h-10 border rounded-md px-3 text-sm" data-testid="search-trailer">
                  <option value="">All Types</option>
                  {Object.entries(TRAILER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSearch} disabled={geocoding} className="bg-red-600 hover:bg-red-700 text-white h-10 px-6" data-testid="search-btn">
                  {geocoding ? 'Locating...' : <><Search className="w-4 h-4 mr-2" /> Search</>}
                </Button>
                {(searchTown || searchTrailer) && (
                  <Button onClick={handleClearSearch} variant="outline" className="h-10 px-3" data-testid="clear-search-btn">
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            {searchCoords && searchTown && (
              <p className="text-xs text-gray-500">
                Showing transporters within <strong>{searchRadius} km</strong> of <strong>{searchTown}</strong>
              </p>
            )}

            {/* Available Now filter */}
            <div className="flex items-center gap-3 pt-2 border-t">
              <button
                type="button"
                onClick={() => setAvailableOnly(!availableOnly)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  availableOnly ? 'bg-green-600 border-green-600 text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                data-testid="available-only-filter"
              >
                <span className={`w-2 h-2 rounded-full ${availableOnly ? 'bg-white' : 'bg-green-500'}`}></span>
                🟢 Available to Load only
              </button>
              <p className="text-xs text-gray-500">
                {availableOnly ? `${filteredListings.length} available` : `${listings.length} total`}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Map of Australia */}
      <div className="container mx-auto px-4 mt-6">
        <Card className="p-2">
          <div className="w-full h-[380px] rounded relative">
            <div ref={mapRef} className="absolute inset-0 rounded" data-testid="transporters-map"></div>
            {!mapLoaded && (
              <p className="absolute inset-0 flex items-center justify-center text-gray-500 bg-gray-100 rounded">
                Loading map...
              </p>
            )}
          </div>
        </Card>
      </div>

      {/* Results */}
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <p className="text-center text-gray-500 py-12">Loading transporters...</p>
        ) : listings.length === 0 ? (
          <div className="text-center py-16">
            <Truck className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Transporters Found</h3>
            <p className="text-gray-500">No carriers are currently listed in this area. Try broadening your search or check back soon.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4">{listings.length} transporter{listings.length !== 1 ? 's' : ''} available</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredListings.map(listing => (
                <Card key={listing.id} className="p-5 hover:shadow-md transition-shadow" data-testid={`listing-${listing.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-[#1e3a5f] text-lg">{listing.company_name || 'Independent Carrier'}</h3>
                        {listing.fleet_number && (
                          <span
                            className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#1e3a5f] text-white font-mono"
                            data-testid={`public-fleet-badge-${listing.id}`}
                          >
                            #{listing.fleet_number}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{listing.location_suburb}, {listing.location_state}</span>
                      </div>
                    </div>
                    {listing.distance_km != null && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">{listing.distance_km} km</span>
                    )}
                  </div>

                  {/* Trailer Types */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {(listing.trailer_types || []).map(t => (
                      <span key={t} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{TRAILER_LABELS[t] || t}</span>
                    ))}
                  </div>

                  {/* Details */}
                  <div className="text-sm text-gray-600 space-y-1 mb-3">
                    {listing.max_weight_kg && <p><strong>Max Weight:</strong> {listing.max_weight_kg.toLocaleString()} kg</p>}
                    {listing.preferred_regions?.length > 0 && <p><strong>Regions:</strong> {listing.preferred_regions.join(', ')}</p>}
                    {listing.preferred_routes && <p><strong>Routes:</strong> {listing.preferred_routes}</p>}
                    {listing.special_capabilities && <p><strong>Capabilities:</strong> {listing.special_capabilities}</p>}
                    {(listing.available_from || listing.available_until) && (
                      <p className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> <strong>Available:</strong> {formatDate(listing.available_from)} {listing.available_until ? `— ${formatDate(listing.available_until)}` : '→ Ongoing'}</p>
                    )}
                  </div>

                  {listing.notes && <p className="text-xs text-gray-500 italic mb-3">{listing.notes}</p>}

                  <div className="flex gap-2 mb-2">
                    <Button onClick={() => openRequestModal(listing)} className="flex-1 bg-[#1e3a5f] hover:bg-[#15304f] text-white" data-testid={`request-btn-${listing.id}`}>
                      <Send className="w-4 h-4 mr-2" /> Request a Quote
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Share:</span>
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://www.trucknsendit.com/transporters')}&quote=${encodeURIComponent(`Transporter available in ${listing.location_suburb}, ${listing.location_state} — ${(listing.trailer_types || []).map(t => TRAILER_LABELS[t] || t).join(', ')}. Find carriers on Truckn Send It!`)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs bg-blue-600 text-white px-2 py-1 rounded-full hover:bg-blue-700 transition-colors"
                      data-testid={`share-fb-${listing.id}`}
                    >
                      <Facebook className="w-3 h-3" /> Facebook
                    </a>
                    <button
                      onClick={() => {
                        const text = `Transporter available in ${listing.location_suburb}, ${listing.location_state}\nTrailers: ${(listing.trailer_types || []).map(t => TRAILER_LABELS[t] || t).join(', ')}${listing.max_weight_kg ? `\nMax Weight: ${listing.max_weight_kg.toLocaleString()} kg` : ''}${listing.available_from ? `\nAvailable: ${new Date(listing.available_from).toLocaleDateString('en-AU')}${listing.available_until ? ` — ${new Date(listing.available_until).toLocaleDateString('en-AU')}` : ' onwards'}` : ''}\n\nFind transporters: https://www.trucknsendit.com/transporters\n\n#TrucknSendIt #FreightAustralia #Transport`;
                        navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard!')).catch(() => prompt('Copy this:', text));
                      }}
                      className="flex items-center gap-1 text-xs bg-gray-600 text-white px-2 py-1 rounded-full hover:bg-gray-700 transition-colors"
                      data-testid={`share-copy-${listing.id}`}
                    >
                      <Share2 className="w-3 h-3" /> Copy
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Request Modal */}
      {showRequestModal && selectedListing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b bg-[#1e3a5f] text-white rounded-t-xl">
              <h2 className="text-lg font-bold">Request Transport Quote</h2>
              <button onClick={() => setShowRequestModal(false)} className="hover:bg-white/20 rounded-full p-1"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmitRequest} className="p-5 space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                Requesting quote from <strong>{selectedListing.company_name || 'Independent Carrier'}</strong> in {selectedListing.location_suburb}, {selectedListing.location_state}. The carrier will respond with a price, which will be reviewed by admin before you see it.
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label>Your Name *</Label><Input value={requestForm.shipper_name} onChange={e => setRequestForm(p => ({...p, shipper_name: e.target.value}))} required data-testid="req-name" /></div>
                <div><Label>Your Email *</Label><Input type="email" value={requestForm.shipper_email} onChange={e => setRequestForm(p => ({...p, shipper_email: e.target.value}))} required data-testid="req-email" /></div>
                <div><Label>Phone</Label><Input value={requestForm.shipper_phone} onChange={e => setRequestForm(p => ({...p, shipper_phone: e.target.value}))} data-testid="req-phone" /></div>
                <div><Label>Company</Label><Input value={requestForm.shipper_company} onChange={e => setRequestForm(p => ({...p, shipper_company: e.target.value}))} data-testid="req-company" /></div>
              </div>

              <div className="border-t pt-3">
                <h3 className="font-semibold text-[#1e3a5f] mb-2 flex items-center gap-1"><Package className="w-4 h-4" /> Load Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Pickup Location *</Label><Input value={requestForm.pickup_location} onChange={e => setRequestForm(p => ({...p, pickup_location: e.target.value}))} placeholder="Sydney, NSW" required data-testid="req-pickup" /></div>
                  <div><Label>Dropoff Location *</Label><Input value={requestForm.dropoff_location} onChange={e => setRequestForm(p => ({...p, dropoff_location: e.target.value}))} placeholder="Melbourne, VIC" required data-testid="req-dropoff" /></div>
                  <div><Label>Cargo Type *</Label><Input value={requestForm.cargo_type} onChange={e => setRequestForm(p => ({...p, cargo_type: e.target.value}))} placeholder="Machinery, General, etc." required data-testid="req-cargo" /></div>
                  <div><Label>Pickup Date</Label><Input type="date" value={requestForm.pickup_date} onChange={e => setRequestForm(p => ({...p, pickup_date: e.target.value}))} data-testid="req-date" /></div>
                  <div><Label>Weight</Label><Input value={requestForm.weight} onChange={e => setRequestForm(p => ({...p, weight: e.target.value}))} placeholder="e.g. 5000 kg" /></div>
                  <div><Label>Dimensions</Label><Input value={requestForm.dimensions} onChange={e => setRequestForm(p => ({...p, dimensions: e.target.value}))} placeholder="L x W x H" /></div>
                </div>
                <div className="mt-3"><Label>Description / Special Requirements</Label><Textarea value={requestForm.cargo_description} onChange={e => setRequestForm(p => ({...p, cargo_description: e.target.value}))} rows={2} placeholder="Any details the carrier should know..." /></div>
                <div className="mt-3"><Label>Message to Carrier</Label><Textarea value={requestForm.message} onChange={e => setRequestForm(p => ({...p, message: e.target.value}))} rows={2} placeholder="Optional message..." /></div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={submitting} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="submit-request-btn">
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowRequestModal(false)} className="flex-1">Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Transporters;
